import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { MISSION_IMG, SERVICES } from "@/data/services";
import { Button } from "@/components/ui/button";
import { Target, Globe2, Users, Cpu, Leaf, CheckCircle2, ArrowRight, Factory, Candy, Droplets, Cookie, IceCream, CandyCane, Wine, Wheat, Truck, BookOpen, Landmark, Hammer } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.07 } }),
};

const MISSION_POINTS = [
  "Provide effective utilization of labor by encouraging entrepreneurship, developing enterprises, talents and employing different skills.",
  "Encourage productive usage of technology by our clients.",
  "Conduct seminars and connect domestic producers and social projects to various international agencies for training and capital access.",
  "Provide all facets of support for our clients on matters of IT, Research, Environmental and Technical Consultancies.",
  "Focus relentlessly on quality work and client satisfaction.",
];

const VALUES = [
  [Target, "Excellence", "A relentless commitment to quality in every engagement."],
  [Users, "Integrity", "Honest, ethical dealings with communities, businesses and families."],
  [Cpu, "Innovation", "Productive use of technology to move our clients forward."],
  [Leaf, "Sustainability", "Conservation and reduction of our clients' carbon footprint."],
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="bg-brand-grey min-h-screen">
      <SEO
        title="About Us | Tanz-Bridge International LTD"
        description="Tanz-Bridge International LTD is a technical consultancy linking producers to the global market, with operations in Tanzania and the United States."
        path="/about"
      />
      <Navbar onOrder={() => navigate("/#services")} />

      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden grain bg-brand-dark">
        <div className="absolute inset-0 z-0">
          <img src={MISSION_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/90 to-brand-dark/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <motion.p initial="hidden" animate="show" variants={fadeUp} className="text-sm tracking-[0.3em] uppercase font-semibold text-brand-yellow mb-5">
            About Us
          </motion.p>
          <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold tracking-tighter text-white max-w-3xl leading-tight">
            An intermediary bridging <span className="text-brand-yellow">producers to the world</span>.
          </motion.h1>
          <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp} className="mt-6 text-lg text-gray-300 max-w-3xl leading-relaxed">
            Tanz – BRIDGE International LTD provides technical consultancy services in NYC and Tanzania for both
            profit and non-profit organizations.
          </motion.p>
        </div>
      </section>

      {/* COMPANY PROFILE */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="text-sm tracking-[0.2em] uppercase font-semibold text-brand-dark mb-3">Company profile</p>
          <h2 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight text-brand-dark mb-6">Who we are</h2>
          <p className="text-gray-600 font-medium leading-relaxed mb-4">
            Tanz – BRIDGE International LTD is an intermediate company providing Technical Consultancy Services in
            Tanzania on Production and Distribution, Information Technology, Research, Marketing and Trading on
            industrial and agricultural products, linking the producer and consumer on a global market.
          </p>
          <p className="text-gray-600 font-medium leading-relaxed mb-8">
            Our mission is to encourage entrepreneurship, developing enterprises and connect domestic producers to
            various internal and international markets.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button data-testid="about-services-btn" onClick={() => navigate("/#services")}
              className="bg-brand-blue hover:bg-brand-bluehover text-white rounded-sm px-6 py-6">
              Explore our services <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button data-testid="about-team-btn" onClick={() => navigate("/team")} variant="outline"
              className="border-gray-300 bg-transparent text-brand-dark hover:bg-white rounded-sm px-6 py-6">
              Meet the team
            </Button>
          </div>
        </div>
        <div className="relative">
          <img src={SERVICES[2].image} alt="Tanz-Bridge" className="rounded-lg border border-gray-200 shadow-lg w-full object-cover h-[360px]" />
          <div className="absolute -bottom-6 -left-6 bg-brand-yellow text-brand-dark rounded-lg p-6 max-w-[200px] shadow-2xl">
            <p className="text-4xl font-heading">2</p>
            <p className="text-sm mt-1 font-medium">Continents served — Tanzania & the USA</p>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-brand-dark py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm tracking-[0.2em] uppercase font-semibold text-white mb-3">Our mission</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight text-white mb-6">
              Encouraging entrepreneurship, developing enterprises.
            </h2>
            <p className="text-gray-300 font-medium leading-relaxed">
              We connect domestic producers and social projects to international agencies for training and capital
              access — while championing quality and client satisfaction in everything we do.
            </p>
          </div>
          <div className="space-y-4">
            {MISSION_POINTS.map((m, i) => (
              <motion.div key={m} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="flex items-start gap-4 rounded-lg bg-white/5 border border-white/10 p-5">
                <CheckCircle2 size={20} className="text-brand-yellow shrink-0 mt-0.5" />
                <span className="text-gray-200">{m}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm tracking-[0.2em] uppercase font-semibold text-brand-dark mb-3">Our values</p>
          <h2 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight text-brand-dark">
            The principles behind our commitment for excellence.
          </h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map(([Icon, title, desc], i) => (
            <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              className="rounded-lg bg-brand-card border border-gray-200 shadow-sm p-6">
              <div className="h-11 w-11 rounded-sm bg-brand-blue/10 border border-brand-blue/25 flex items-center justify-center mb-4">
                <Icon size={20} className="text-brand-blue" />
              </div>
              <h3 className="text-lg font-heading text-brand-dark mb-2">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISION 2025/2026 */}
      <section className="bg-brand-dark grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="max-w-3xl mb-14">
            <p className="text-sm tracking-[0.2em] uppercase font-semibold text-brand-yellow mb-3 flex items-center gap-2">
              <Factory size={16} /> Vision 2026 / 2027 &amp; 2027 / 2028
            </p>
            <h2 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight text-white mb-6">
              Growing from consultancy into industry &amp; production.
            </h2>
            <p className="text-gray-300 font-medium leading-relaxed">
              Our next chapter centres on establishing a production factory in Rungwe District, Mbeya Region —
              turning the region's rich agricultural output into finished, branded Tanzanian products. Rungwe is
              one of Tanzania's leading milk-producing districts, with cocoa from Rungwe and Kyela and abundant
              cassava — the raw materials for a value chain built close to its source.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              [Candy, "TANZ PIPI", "Milk & cocoa sweets produced from locally sourced Rungwe milk and cocoa."],
              [Droplets, "TANZ MILK", "Packaged milk in affordable retail sizes — from TZS 1,000 to TZS 2,000."],
              [Cookie, "TANZ CRIPS", "Banana, cassava and potato chips made from our own farm supply chain."],
              [IceCream, "TANZ YOUGUT", "Creamy yoghurt made from fresh Rungwe milk, offered in a range of natural flavours."],
              [CandyCane, "TANZ COCOA CHOCOLATE", "Rich chocolate crafted from locally grown cocoa, supporting Rungwe farmers."],
              [Wine, "TANZ WINE", "Locally produced wine crafted from Rungwe's fruit harvest, bottled for domestic and export markets."],
            ].map(([Icon, title, desc], i) => (
              <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="rounded-lg bg-white/5 border border-white/10 p-6">
                <div className="h-11 w-11 rounded-sm bg-brand-yellow/15 border border-brand-yellow/30 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-brand-yellow" />
                </div>
                <h3 className="text-lg font-heading text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-300 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPANDING WHAT WE DO */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm tracking-[0.2em] uppercase font-semibold text-brand-dark mb-3">Beyond consultancy</p>
          <h2 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight text-brand-dark">
            Expanding what we do across the value chain.
          </h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Wheat, "Crops & Livestock Trade", "Direct trade in bananas, avocados and cocoa, plus livestock and thousands of litres of milk supplied to processors."],
            [Hammer, "Hardware & Building Materials", "Supply of sand, bricks, iron sheets, ceiling boards and related construction materials."],
            [BookOpen, "Books & Stationery", "Books, paper, notebooks, printers and photocopier machines for offices and institutions."],
            [Landmark, "Government Partnerships", "Licensed intermediary with the Mining Commission and LATRA for registration across Mbeya and Iringa."],
          ].map(([Icon, title, desc], i) => (
            <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              className="rounded-lg bg-brand-card border border-gray-200 shadow-sm p-6">
              <div className="h-11 w-11 rounded-sm bg-brand-blue/10 border border-brand-blue/25 flex items-center justify-center mb-4">
                <Icon size={20} className="text-brand-blue" />
              </div>
              <h3 className="text-lg font-heading text-brand-dark mb-2">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
